import { useQuery } from '@apollo/client'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { GetCandidateEmailAudits } from 'recruiter/graphql/candidateEmailAudit.gql'
import { LoadingPanel } from 'shared/components/LoadingPanel'

const EMAIL_TYPE_LABELS: Record<string, string> = {
    scheduleInterviewEmail: 'Schedule Interview',
    sendPortalInvitationEmail: 'Portal Invitation',
    sendConfirmRegistrationEmail: 'Confirm Registration',
    sendResetPasswordEmail: 'Reset Password',
    reminderStartRegistration: 'Reminder: Start Registration (14d)',
    reminderFinishRegistration: 'Reminder: Complete Registration (5d)',
    reminderCompleteRegistration14Day: 'Reminder: Complete Registration (14d)',
    reminderScheduleMeeting: 'Reminder: Schedule Meeting',
    registrationOnHold: 'Registration On Hold',
    blast: 'Blast Email',
}

function formatEmailType(emailType: string): string {
    return EMAIL_TYPE_LABELS[emailType] ?? emailType
}

function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

interface EmailCardProps {
    email: {
        id: string
        emailType: string
        subject?: string | null
        recipientEmail: string
        senderEmail?: string | null
        senderName?: string | null
        bodyPlain?: string | null
        bodyHtml?: string | null
        sent: boolean
        createdAt: string
    }
}

function EmailCard({ email }: EmailCardProps) {
    const [expanded, setExpanded] = useState(false)
    const [contentTab, setContentTab] = useState(0)

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: expanded ? 'action.selected' : 'transparent',
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        minWidth={0}
                    >
                        <Chip
                            label={formatEmailType(email.emailType)}
                            size="small"
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                        />
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ minWidth: 0 }}
                        >
                            {email.subject || '(no subject)'}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0 }}
                    >
                        {formatDate(email.createdAt)}
                    </Typography>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    mt={0.5}
                    flexWrap="wrap"
                >
                    <Typography variant="caption" color="text.secondary">
                        To: {email.recipientEmail}
                    </Typography>
                    {email.senderName && (
                        <Typography variant="caption" color="text.secondary">
                            &middot; From: {email.senderName}
                            {email.senderEmail ? ` <${email.senderEmail}>` : ''}
                        </Typography>
                    )}
                    <Chip
                        label={email.sent ? 'Sent' : 'Not Sent'}
                        size="small"
                        color={email.sent ? 'success' : 'warning'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                </Stack>
            </Box>

            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Tabs
                        value={contentTab}
                        onChange={(_e, v) => setContentTab(v)}
                        sx={{ minHeight: 32, mb: 1 }}
                    >
                        <Tab
                            label="Plain Text"
                            sx={{
                                minHeight: 32,
                                py: 0,
                                textTransform: 'none',
                                fontSize: '0.8rem',
                            }}
                        />
                        {email.bodyHtml && (
                            <Tab
                                label="HTML Preview"
                                sx={{
                                    minHeight: 32,
                                    py: 0,
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                }}
                            />
                        )}
                    </Tabs>

                    {contentTab === 0 && (
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                maxHeight: 400,
                                overflow: 'auto',
                            }}
                        >
                            <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'inherit',
                                    m: 0,
                                }}
                            >
                                {email.bodyPlain || '(no content)'}
                            </Typography>
                        </Box>
                    )}

                    {contentTab === 1 && email.bodyHtml && (
                        <iframe
                            sandbox=""
                            srcDoc={email.bodyHtml}
                            style={{
                                width: '100%',
                                height: 300,
                                maxHeight: 400,
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                borderRadius: 4,
                            }}
                            title="Email HTML preview"
                        />
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}

export default function EmailAuditList() {
    const { candidateId } = useParams()

    const { data, loading } = useQuery(GetCandidateEmailAudits, {
        variables: { candidateId: candidateId! },
        skip: !candidateId,
    })

    if (loading) return <LoadingPanel />

    const emails = data?.candidateEmailAudits ?? []

    return (
        <ProfilePanel panelTitle="Email History">
            {emails.length === 0 ? (
                <Typography color="text.secondary">
                    No email history for this candidate.
                </Typography>
            ) : (
                <Stack gap={1}>
                    {emails.map((email) => (
                        <EmailCard key={email.id} email={email} />
                    ))}
                </Stack>
            )}
        </ProfilePanel>
    )
}
